import { ITEM_TYPES } from '../../constants';
import { num } from './dates';

/**
 * The app's monthly compounding convention: an annual rate is divided by
 * twelve, not geometrically twelfth-rooted. So a stated 7 % compounds to
 * 7.229 % effective over a year.
 *
 * monteCarlo.js deliberately still uses `(1 + r)^(1/12)`, so its bands sit
 * slightly below the deterministic projection for that reason alone. Named here
 * so the next person finds it from the kernel rather than by diffing two month
 * loops. Planned out in launch/PLAN_MONTE_CARLO_REWORK.md item 3 — the fix is
 * for monteCarlo.js to call `stepItemMonth` below, which is what that function's
 * `rates` parameter was built for.
 */
export const monthlyFactor = (annualRate) => 1 + (annualRate || 0) / 12;

/** Compound over a (possibly fractional) number of months. */
export const compoundMonths = (value, annualRate, months) =>
    value * Math.pow(monthlyFactor(annualRate), months);

/**
 * The rates that drive one item, after applying any per-item override (the
 * simulation page's what-if sliders).
 */
export const resolveItemRates = (item, rateOverrides = {}) => {
    const override = rateOverrides[item.id] || {};
    return {
        appreciation: override.annualAppreciationRate ?? num(item.annualAppreciationRate),
        rentalAndCosts: override.rentalAndCostsGrowthRate ?? num(item.rentalAndCostsGrowthRate),
        growth: override.annualGrowthRate ?? num(item.annualGrowthRate),
    };
};

/**
 * Advance one item by one month, in place — the single definition of what a
 * month does to an item.
 *
 * Order is load-bearing: the contribution lands first, then growth applies to
 * the new balance. Withdrawals are floored at the balance, and an emptied
 * investment stops withdrawing.
 *
 * `rates` lets a caller supply already-resolved rates (Monte Carlo samples
 * them per year); omit it and the item's own assumptions are used.
 */
export const stepItemMonth = (item, { rates } = {}) => {
    const itemCurrentValue = num(item.currentValue);
    const resolved = rates || resolveItemRates(item);

    switch (item.type) {
        case ITEM_TYPES.REAL_ESTATE: {
            item.currentValue = itemCurrentValue * monthlyFactor(resolved.appreciation);
            if (resolved.rentalAndCosts !== 0) {
                const factor = monthlyFactor(resolved.rentalAndCosts);
                if (item.isRented) item.monthlyRentalIncome = num(item.monthlyRentalIncome) * factor;
                else item.monthlyCosts = num(item.monthlyCosts) * factor;
            }
            break;
        }
        case ITEM_TYPES.OTHER_INVESTMENT: {
            const effectiveAmount = item.contributionFrequency === 'annually'
                ? num(item.monthlyContribution) / 12
                : num(item.monthlyContribution);

            if (effectiveAmount < 0) {
                const actualWithdrawal = Math.min(Math.abs(effectiveAmount), item.currentValue);
                item.currentValue -= actualWithdrawal;
                if (item.currentValue <= 0) item.monthlyContribution = 0;
            } else if (effectiveAmount > 0) {
                item.currentValue = itemCurrentValue + effectiveAmount;
            }

            if (item.isGrowth) item.currentValue = item.currentValue * monthlyFactor(resolved.growth);
            break;
        }
        case ITEM_TYPES.INCOME:
            if (resolved.growth !== 0) item.monthlyIncome = num(item.monthlyIncome) * monthlyFactor(resolved.growth);
            break;
        case ITEM_TYPES.EXPENSE:
            if (resolved.growth !== 0) item.monthlyExpense = num(item.monthlyExpense) * monthlyFactor(resolved.growth);
            break;
        default:
            break;
    }
    return item;
};

/**
 * Cash: the portfolio's net monthly cashflow lands in it, then the balance
 * earns its interest. `monthlyNetCashflow` must EXCLUDE liquid interest or it
 * is counted twice. Supports a fractional final month, where the cashflow is
 * pro-rated and growth applies for the part-month.
 */
export const stepLiquidMonths = (amount, annualRate, monthlyNetCashflow, months) => {
    const rate = num(annualRate);
    const cashflow = num(monthlyNetCashflow);
    const wholeMonths = Math.floor(months);

    let balance = num(amount);
    for (let i = 0; i < wholeMonths; i++) {
        balance = compoundMonths(balance + cashflow, rate, 1);
    }
    const remainder = months - wholeMonths;
    return compoundMonths(balance + cashflow * remainder, rate, remainder);
};
